import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowDownLeft, ArrowUp, ArrowUpRight, Check } from "lucide-react";
import { formatScore } from "./v2OverviewUtils";
import { MUSCLE_I18N_KEYS, MUSCLE_TOOLTIP_KEYS } from "./v2PriorityUtils";

const ZONE_ICONS = {
  underload: ArrowDownLeft,
  suboptimal: ArrowDown,
  optimal: Check,
  supraoptimal: ArrowUp,
  overload: ArrowUpRight,
};

const ZONE_BADGE = {
  underload: "text-[#9BD3FF] bg-[rgba(155,211,255,0.1)]",
  suboptimal: "text-[#7EA2FF] bg-[rgba(77,132,255,0.12)]",
  optimal: "text-[#00FF8C] bg-[rgba(0,255,140,0.1)]",
  supraoptimal: "text-[#FFA630] bg-[rgba(255,166,48,0.1)]",
  overload: "text-[#FF4D5E] bg-[rgba(255,77,94,0.12)]",
};

const ALIGN = {
  start: "left-0 translate-x-0",
  end: "left-auto right-0 translate-x-0",
  center: "left-1/2 -translate-x-1/2",
};

export default function V2PlayerScaleTooltip({ player, align = "center" }) {
  const { t } = useTranslation();
  const Icon = ZONE_ICONS[player.zone] || Check;
  const tooltipKey = MUSCLE_TOOLTIP_KEYS[player.focusMuscle];
  const muscleKey = MUSCLE_I18N_KEYS[player.focusMuscle] || player.focusMuscle;
  const muscle = tooltipKey
    ? t(`v2.overview.tooltip.muscles.${tooltipKey}`)
    : t(`muscles.${muscleKey}`, { defaultValue: player.focusMuscle || "" });
  const side = player.focusSide ? t(`v2.overview.tooltip.side_${player.focusSide}`) : "";
  const monthScore = player.monthScore ?? player.score;
  const period = t("v2.overview.priority.period_week");
  const summary = !player.focusMuscle || player.zone === "optimal"
    ? t("v2.overview.tooltip.summary_optimal", { period })
    : t("v2.overview.tooltip.summary_focus", { muscle, side, period });

  return (
    <div
      className={`pointer-events-none absolute bottom-[calc(100%+14px)] z-[12] w-[280px] rounded-xl border border-[rgba(98,94,166,0.5)] bg-[#19144e] px-3.5 py-3 text-left leading-snug tracking-normal shadow-[0_16px_40px_rgba(2,0,16,0.55)] ${ALIGN[align] || ALIGN.center}`}
      role="tooltip"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <strong className="block text-[14px] font-bold leading-tight text-[#f6f3ff]">{player.name}</strong>
          <p className="mt-1 text-[12px] leading-relaxed text-[#9b96c9]">
            {t(`v2.overview.positions.${String(player.position || "mil").toLowerCase()}`)}
            <span className="px-1">·</span>
            {t(`v2.overview.${player.zone}`)}
          </p>
        </div>
        <em className="v2-display shrink-0 text-[26px] font-bold not-italic leading-none text-[#f6f3ff]">
          {formatScore(player.score)}
          <small className="text-[13px] font-semibold text-[#8f8abf]">/10</small>
        </em>
      </div>

      <p className="mt-2.5 text-[12px] leading-relaxed text-[#d8d5ff]">{summary}</p>

      <div className="mt-2.5 flex items-center justify-between gap-2">
        <span className={`inline-flex items-center gap-1 rounded-full border border-current px-2 py-[3px] text-[11px] font-bold ${ZONE_BADGE[player.zone] || ZONE_BADGE.optimal}`}>
          <Icon size={12} strokeWidth={2.2} />
          {t(`v2.overview.${player.zone}`)}
        </span>
        <span className="text-[12px] text-[#9b96c9]">
          {t("v2.overview.tooltip.month_trainability")}
          <strong className="ml-1.5 text-[13px] text-[#f6f3ff]">{formatScore(monthScore)}</strong>
        </span>
      </div>
    </div>
  );
}
